import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { EmojiData, EmojiPicker, useTheme } from '@ublend-npm/aulaui-core';
import { operations as reactionOperations } from '@app/store/reactions';
import type { ReactionItemType } from './types';

type ReactionPickerProps = {
  itemId: string;
  itemType: ReactionItemType;
  readOnly?: boolean;
  onRefetch?: () => void | Promise<void>;
};

export const getSelectedEmojiName = (emoji: EmojiData | string) => {
  if (typeof emoji === 'string') return emoji;
  return emoji.short_name || emoji.shortName || emoji.short_names?.[0] || null;
};

const ReactionPicker = ({
  itemId,
  itemType,
  readOnly = false,
  onRefetch,
}: ReactionPickerProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const toggleReaction = useCallback(
    async (emojiName: string) => {
      await dispatch(
        reactionOperations.toggleReaction({ emojiName, itemId, itemType })
      );
      await onRefetch?.();
    },
    [dispatch, itemId, itemType, onRefetch]
  );

  const handleSelect = useCallback(
    (emoji: EmojiData | string) => {
      const emojiName = getSelectedEmojiName(emoji);
      if (emojiName) toggleReaction(emojiName);
    },
    [toggleReaction]
  );

  if (!itemId || readOnly) return null;

  return (
    <EmojiPicker
      buttonAriaLabel="Reaction emojis"
      buttonLabel={null}
      buttonProps={{
        sx: {
          border: 'none',
          height: '40px',
          minHeight: '40px',
          minWidth: '40px',
          width: '40px',
          padding: 0,
          '&:hover': {
            border: 'none',
            backgroundColor: theme.palette.action.hover,
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        },
      }}
      id={`${itemType}-${itemId}-emoji-picker`}
      onSelect={handleSelect}
    />
  );
};

export default ReactionPicker;
